// "Selfie search and face data from 1 November 2026" at /face-data-changes-2026 (English, the source of truth).
// Linked from the face-data campaign email (photohawk-serverless ops/campaigns/face-data-2026-11.json).
//
// Facts (keep in sync): photohawk-serverless docs/face-data-retention.md and src/layers/lib/src/faceRetention.js,
// and the public policy src/content/legal/biometric.en.ts. Facts since 31 Oct 2026 (owner): the workspace / gallery
// chooses how long face data is kept (No expiry by default, or 30 / 90 / 180 days / 12 months after upload); switching
// selfie search off deletes it after 30 days; deleting a gallery deletes it at once; closing the workspace deletes it
// within 24 hours; archiving a gallery deletes nothing. Never claim that the law prescribes a period. No legal advice.
// Never assume the reader's role (no "photographer" / "organiser").

export interface FaceDataCopy {
  seo: { title: string; description: string };
  hero: { badge: string; title: string; titleHighlight: string; subtitle: string; ctaPolicy: string; ctaHow: string };
  /** The animated timeline. */
  timeline: {
    title: string;
    intro: string;
    label: string;
    caption: string;
    steps: { id: "upload" | "search" | "delete" | "carry"; when: string; title: string; body: string }[];
    /** Short labels inside the animation (keep them short: they sit in a 390px-wide figure). */
    scene: { selfie: string; upload: string; search: string; months: string; deleted: string; carry: string; match: string };
  };
  /** The "face template dissolving" illustration. */
  dissolve: {
    title: string;
    intro: string;
    label: string;
    caption: string;
    scene: { photo: string; template: string; bib: string; sale: string; gone: string };
  };
  november: { title: string; intro: string; points: { id: string; title: string; body: string }[] };
  unchanged: { title: string; intro: string; items: { id: string; text: string }[] };
  buyers: { title: string; intro: string; items: { id: string; text: string }[]; tip: string };
  why: { title: string; lead: string; paragraphs: string[]; laws: { id: string; name: string; text: string }[]; ourChoice: string; note: string };
  faq: { title: string; items: { id: string; q: string; a: string }[] };
  /** `allProducts`: the link to /face-data-policy (Photohawk Studio and Photohawk Family). */
  cta: { title: string; body: string; policy: string; contact: string; allProducts: string };
  pauseLabel: string;
  playLabel: string;
}

const en: FaceDataCopy = {
  seo: {
    title: "Selfie search and face data from 1 November | Photohawk",
    description:
      "From 1 November 2026, you choose how long the face data behind selfie search is kept: No expiry (the default) or a period after each photo's upload. Photos, sales, downloads and bib search are never affected. Here's how it works.",
  },
  hero: {
    badge: "From 1 November 2026",
    title: "Selfie search and face data:",
    titleHighlight: "what's changing",
    subtitle:
      "From 1 November, you decide how long the face data that makes selfie search work is kept, for your whole workspace or gallery by gallery. The default is No expiry. Choose a period and each photo's face data is deleted automatically that long after it was uploaded. Your photos stay exactly where they are.",
    ctaPolicy: "Read the face-data policy",
    ctaHow: "See how it works",
  },
  timeline: {
    title: "How it works",
    intro: "If you choose a period, every photo follows the same simple path. The clock is per photo: it starts when that photo is uploaded. On No expiry, the default, face data stays until you delete it or switch selfie search off.",
    label: "Animated timeline: a photo is uploaded, selfie search finds it for the period chosen (here 12 months), its face data is then deleted, and the photo carries on.",
    caption:
      "With a period chosen (here 12 months): a photo is uploaded, selfie search finds it for that long, then its face data is deleted automatically. The photo itself, its sales and bib search carry on.",
    steps: [
      { id: "upload", when: "Day 1", title: "A photo is uploaded", body: "Faces in it are turned into face data, so buyers can find it with a selfie." },
      { id: "search", when: "For the period you chose", title: "Selfie search finds it", body: "Buyers take a selfie and see every photo they're in, just like today." },
      { id: "delete", when: "When it ends", title: "Its face data is deleted", body: "Automatically and permanently. The photo is never face-indexed again." },
      { id: "carry", when: "After that", title: "The photo carries on", body: "Buyers can still find it by bib number, time or browsing, and buy and download it." },
    ],
    scene: {
      selfie: "Selfie",
      upload: "Uploaded",
      search: "Selfie search",
      months: "12 months",
      deleted: "Face data deleted",
      carry: "Photo stays",
      match: "Match",
    },
  },
  dissolve: {
    title: "Face data isn't the photo",
    intro:
      "Face data is a set of numbers describing the faces in a photo: a face template. It's only used to match a buyer's selfie. Deleting it removes the template and nothing else.",
    label: "Animated illustration: a photo with its face template beside it; the template dissolves while the photo, its bib number and its sale stay.",
    caption: "The face template dissolves. The photo, its bib number and its sale don't change.",
    scene: { photo: "Photo", template: "Face template", bib: "Bib 1234", sale: "Sold", gone: "Deleted" },
  },
  november: {
    title: "What happens on 1 November",
    intro: "Nothing is deleted because of its age unless you choose a period.",
    points: [
      {
        id: "backlog",
        title: "No expiry, unless you choose a period",
        body: "Every workspace and gallery starts on No expiry. You can choose 30, 90 or 180 days or 12 months in Workspace settings → Face search, and each gallery can choose differently in its own selfie search settings.",
      },
      {
        id: "daily",
        title: "If you choose a period",
        body: "An automated job runs every day and deletes each photo's face data once it's older than the period you chose. If you change the period, the new one applies from the next check.",
      },
      {
        id: "ended",
        title: "When all of a gallery's face data has gone",
        body: "Selfie search ends for that gallery. Buyers who try it see a short note that points them to bib search and browsing instead.",
      },
      {
        id: "off",
        title: "Selfie search switched off",
        body: "Face data is deleted 30 days after a gallery's selfie search is switched off; switch it back on within those 30 days and nothing is deleted. For galleries already switched off before 1 November, the 30 days start from 1 November at the earliest. Archiving a gallery doesn't delete its face data: an archived gallery keeps its selfie search.",
      },
      {
        id: "workspace",
        title: "Deleting a gallery, or closing your workspace",
        body: "Deleting a gallery deletes its face data straight away. If you close your Photohawk workspace, all its face data is deleted within 24 hours.",
      },
    ],
  },
  unchanged: {
    title: "What doesn't change",
    intro: "Deleting face data never deletes a photo. Everything else carries on as it is today:",
    items: [
      { id: "photos", text: "Your photos and galleries" },
      { id: "sales", text: "Sales, orders and your earnings" },
      { id: "downloads", text: "Downloads, including free-download galleries" },
      { id: "bib", text: "Bib and number search, and text search" },
      { id: "time", text: "Time search and browsing" },
      { id: "recent", text: "Selfie search, for as long as you keep a gallery's face data" },
    ],
  },
  buyers: {
    title: "What your buyers will notice",
    intro: "On No expiry, nothing. If you choose a period, most buyers still won't notice: they look for their photos in the days and weeks after an event.",
    items: [
      { id: "older", text: "With a period, selfie search won't find photos uploaded longer ago than that period." },
      { id: "other", text: "They can still find those photos by bib number, time or browsing the gallery, and buy and download them as usual." },
      { id: "note", text: "In a gallery whose face data has all been deleted, selfie search shows a short note pointing them to the other ways to search." },
      { id: "selfies", text: "Selfies used to search are still never stored, as today." },
    ],
    tip: "A good habit: remind buyers to search soon after each event, in your event emails or social posts.",
  },
  why: {
    title: "Why you choose",
    lead: "You decide how face search is used in your galleries, so you decide how long its face data is kept.",
    paragraphs: [
      "Face templates are biometric data, which privacy laws treat as some of the most sensitive personal data there is. Those laws ask that it's kept no longer than it's needed. How long that is depends on your events and where they take place, so the period is yours to set: No expiry, or 30, 90 or 180 days or 12 months after each photo's upload.",
      "If your galleries are covered by one of these laws, choose a period that fits it:",
    ],
    laws: [
      { id: "gdpr", name: "GDPR and UK GDPR", text: "Biometric data used to identify people is a special category of personal data, and the storage-limitation principle says it must be kept no longer than necessary." },
      { id: "bipa", name: "Illinois (BIPA)", text: "Requires a public written retention schedule and destruction of biometric data once its purpose is met, with fixed deadlines." },
      { id: "cubi", name: "Texas (CUBI)", text: "Biometric identifiers must be destroyed within a reasonable time, and no later than a year after the purpose for collecting them ends." },
      { id: "co", name: "Colorado", text: "Biometric data must be deleted once its purpose is met, with fixed outer deadlines and a yearly review." },
      { id: "wa", name: "Washington", text: "Biometric identifiers may be kept no longer than reasonably necessary to provide the service." },
    ],
    ourChoice:
      "What Photohawk does whatever you choose: face data is deleted when you delete a gallery or a photo, 30 days after selfie search is switched off, within 24 hours of a workspace closing, and within 30 days when someone asks. Once a year we also review whether the face data we hold is still needed.",
    note: "This page explains how Photohawk handles face data. It isn't legal advice.",
  },
  faq: {
    title: "Questions",
    items: [
      { id: "photos", q: "Will any of my photos be deleted?", a: "No. Only the face data used for selfie search is ever deleted. Photos, galleries, sales, orders and downloads are not affected." },
      { id: "action", q: "Do I need to do anything?", a: "No. Every gallery stays on No expiry unless you choose a period. If your galleries need a limit, set one in Workspace settings → Face search." },
      { id: "longer", q: "Can I keep face data for longer, or for less time?", a: "Yes. No expiry is the default. You can choose 30, 90 or 180 days or 12 months for your whole workspace in Workspace settings → Face search, and each gallery can choose differently in its own selfie search settings." },
      { id: "sooner", q: "Can face data be deleted sooner?", a: "Yes. Choose a period, or switch off selfie search for a gallery: its face data is then deleted 30 days later, and switching it back on within those 30 days cancels that. Deleting a gallery deletes its face data straight away. Archiving a gallery doesn't delete it." },
      { id: "request", q: "What if someone asks for their face data to be deleted?", a: "Anyone can ask at any time, in the gallery (selfie search, then \"Make a request\") or by emailing support@photohawk.com. Their face data is deleted within 30 days, and you can also do it straight away from the gallery's selfie search settings." },
      { id: "find", q: "How will buyers find photos whose face data is deleted?", a: "By bib or number search, time search or browsing the gallery, exactly as today. Only selfie search stops finding them." },
      { id: "selfies", q: "Are buyers' selfies stored?", a: "No. A selfie is used only for that one search and then discarded, as it is today." },
      { id: "plan", q: "Does this depend on my plan?", a: "No. It's the same for every plan and every gallery." },
    ],
  },
  cta: {
    title: "The full details",
    body: "Our face-data policy sets out exactly what face data we keep, for how long, and how it's deleted. Questions? Email us and a real person will reply.",
    policy: "Read the face-data policy",
    contact: "Email support@photohawk.com",
    allProducts: "Face data policy for Photohawk Studio and the Photohawk app",
  },
  pauseLabel: "Pause animation",
  playLabel: "Play animation",
};

export default en;
